import { useCallback, useMemo } from "react";
import {
  FolderTreeItem,
  useAccountRestriction,
  useBulkRestoreAssets,
  useBulkZapActionPermissions,
  useTrashEnabledAssetPrefixes,
  useUserAccountRole,
} from "@zapier/client";
import {
  BulkModal,
  useBulkActionLimitGuard,
  useEmitZapBulkActionEvent,
  ZapModal,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import { FOLDER_TRASH_ID, useNotification } from "@zapier/utils";

import {
  type ActionContext,
  createBulkActionRegistry,
  resolveActions,
} from "./bulkActionRegistry";
import {
  type SingleRestore,
  useRestoreNotification,
} from "./useRestoreNotification";

interface Input {
  folder?: FolderTreeItem;
  folderId?: string;
  setCurrentZapModal: (modal: ZapModal) => void;
  setSelectedAssetIds: (ids: string[]) => void;
  setCurrentBulkModal: (modal: BulkModal) => void;
  onMoveClicked?: (ids: string[]) => void;
  onCopyToAnotherAccountClicked: (ids: string[]) => void;
  isCopyToAnotherAccountVisible: (ids: string[]) => boolean;
  /** Resolves a single restored asset's name + previous folder for the toast. */
  resolveRestoreMeta?: (id: string) => SingleRestore | undefined;
}

export function useBulkSelectItems({
  setSelectedAssetIds,
  setCurrentZapModal,
  setCurrentBulkModal,
  onMoveClicked,
  onCopyToAnotherAccountClicked,
  isCopyToAnotherAccountVisible,
  folder,
  folderId,
  resolveRestoreMeta,
}: Input) {
  const { isPublishApprovalEnabled, isLoading: isAccountRestrictionLoading } =
    useAccountRestriction();
  const { isAdmin, isSuperAdmin, isAccountOwner } = useUserAccountRole();
  // Fails closed while the restriction is still loading, rather than assuming unrestricted.
  const isDeleteAllowed =
    isAdmin ||
    isSuperAdmin ||
    isAccountOwner ||
    (!isPublishApprovalEnabled && !isAccountRestrictionLoading);
  const tAction = useTranslations("buttons");
  const trashEnabledPrefixes = useTrashEnabledAssetPrefixes();
  const { createNotification } = useNotification();
  const notifyIfOverBulkLimit = useBulkActionLimitGuard("restore");
  const bulkRestoreAssets = useBulkRestoreAssets({
    folderId: folderId ?? folder?.id,
  });
  const { notifyRestoreSuccess } = useRestoreNotification({
    folderId: folderId ?? folder?.id,
  });

  const { emit } = useEmitZapBulkActionEvent();

  const { canBulkChangeOwner } = useBulkZapActionPermissions({
    folder,
  });

  const isTrashFolder = folderId === FOLDER_TRASH_ID;
  const currentContext: ActionContext = isTrashFolder ? "trash" : "folder";

  const handleBulkRestore = useCallback(
    async (ids: string[]) => {
      if (notifyIfOverBulkLimit(ids.length)) {
        return;
      }

      try {
        const { succeededAssetIds, failedAssetIds } =
          await bulkRestoreAssets.mutateAsync({ assetIds: ids });

        if (failedAssetIds.length === 0) {
          const single =
            succeededAssetIds.length === 1
              ? resolveRestoreMeta?.(succeededAssetIds[0])
              : undefined;
          notifyRestoreSuccess({ succeededAssetIds, single });
        } else {
          createNotification({
            message: tAction("restore-partial", {
              succeededCount: succeededAssetIds.length,
              failedCount: failedAssetIds.length,
            }),
            type: "warning",
          });
        }
      } catch {
        createNotification({
          message: tAction("restore-error"),
          type: "error",
        });
      }
    },
    [
      bulkRestoreAssets,
      createNotification,
      notifyIfOverBulkLimit,
      notifyRestoreSuccess,
      resolveRestoreMeta,
      tAction,
    ],
  );

  const registryParameters = useMemo(
    () => ({
      canBulkChangeOwner,
      trashEnabledPrefixes,
      isDeleteAllowed,
      isTrashFolder,
      setSelectedAssetIds,
      setCurrentZapModal,
      setCurrentBulkModal,
      onMoveClicked,
      onCopyToAnotherAccountClicked,
      isCopyToAnotherAccountVisible,
      handleBulkRestore: (ids: string[]) => void handleBulkRestore(ids),
      emit,
    }),
    [
      canBulkChangeOwner,
      trashEnabledPrefixes,
      isDeleteAllowed,
      isTrashFolder,
      setSelectedAssetIds,
      setCurrentZapModal,
      setCurrentBulkModal,
      onMoveClicked,
      onCopyToAnotherAccountClicked,
      isCopyToAnotherAccountVisible,
      handleBulkRestore,
      emit,
    ],
  );

  const bulkSelectItems = useMemo(() => {
    const bulkActionRegistry = createBulkActionRegistry(registryParameters);
    return resolveActions(
      bulkActionRegistry,
      currentContext,
      tAction as (key: string) => string,
    );
  }, [registryParameters, currentContext, tAction]);

  return bulkSelectItems;
}
